/**
 * One tone's block of flagged evidence within the incorrect-evidence summary.
 *
 * Renders a section tag followed by each flagged fact (its `DocChunk#NN`
 * references kept inline as clickable chips, its first letter capitalized) and
 * its optional per-evidence correction (prefixed with the correction arrow glyph).
 * @module FlaggedEvidenceGroup
 */
import { Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import React from "react";
import { CorrectionArrowIcon } from "../../../svg/correction-arrow-icon";
import { usePdfNavigation } from "../context/PdfNavigationContext";
import { ChunkLocation } from "../utils/evidenceText";
import { EvidenceText } from "./EvidenceText";

/** A flagged fact paired with its (optional) reviewer correction. */
export interface FlaggedEntry {
  /** The flagged evidence fact text. */
  text: string;
  /** The reviewer's correction for the fact, or an empty string when none. */
  correction: string;
}

/** Props for {@link FlaggedEvidenceGroup}. */
interface FlaggedEvidenceGroupProps {
  /** Section heading shown in the tag. */
  title: string;
  /** Tag background colour. */
  background: string;
  /** Tag text colour. */
  color: string;
  /** Flagged facts (with corrections) to render. */
  entries: FlaggedEntry[];
  /**
   * Chunk-location lookup so a flagged fact's `DocChunk#N` reference resolves to
   * a clickable page. Absent when the parent item has no locations.
   */
  locations?: Map<number, ChunkLocation>;
}

/**
 * Renders one tone's flagged evidence block, or null when nothing is flagged.
 *
 * @param props - Section title, tag colours, the flagged entries, and chunk locations
 * @returns The rendered sub-section, or null when empty
 */
export const FlaggedEvidenceGroup: React.FC<FlaggedEvidenceGroupProps> = ({
  title,
  background,
  color,
  entries,
  locations,
}) => {
  const { goToPage } = usePdfNavigation();
  if (!entries.length) return null;
  return (
    <div className="flex flex-col gap-2 self-stretch">
      <div className="flex">
        <Tags label={title} backgroundColor={background} textColor={color} />
      </div>
      <ul className="flex flex-col gap-2 self-stretch">
        {entries.map((entry, index) => (
          <li key={index} className="flex flex-col gap-1 self-stretch">
            <span className="text-[0.6875rem] font-normal leading-[1.03125rem] text-[#0F0F0F]">
              <EvidenceText
                text={entry.text}
                locations={locations}
                onNavigate={goToPage}
              />
            </span>
            {entry.correction.trim().length > 0 && (
              <div className="flex items-start gap-1 self-stretch">
                <CorrectionArrowIcon className="mx-[0.3rem] my-[0.125rem] h-[0.875rem] w-[0.875rem] shrink-0" />
                <span className="text-[0.6875rem] font-normal leading-[1.125rem] text-[#0F0F0F] [font-feature-settings:'ss01'] [font-kerning:none]">
                  {entry.correction}
                </span>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};
